import { toBnDigits } from '../../../../../utils/bnFormat';
import {
    COLOR_STYLE_IDS,
    DEFAULT_COLOR_ADJUST,
    normalizeColorAdjust,
    pageHasColorAdjust,
} from '../../pdf/shared/pdfPageEditEngine';

/**
 * Style chips + fine-tune sliders for per-page color correction.
 */
export default function PdfPageColorPanel({
    value,
    onChange,
    t,
    onReset,
    onCancel,
    onSave,
    showReset,
}) {
    const color = normalizeColorAdjust(value);

    const setField = (patch) => {
        onChange({
            ...color,
            ...patch,
            colorStyle: 'custom',
        });
    };

    const applyStyle = (styleId) => {
        if (styleId === 'none') {
            onChange({ ...DEFAULT_COLOR_ADJUST });
            return;
        }
        const preset = COLOR_STYLE_IDS[styleId];
        if (!preset) return;
        onChange({ ...preset });
    };

    const styleButtons = [
        { id: 'none', label: t('pdfPageEditor.styles.none') },
        { id: 'enhance', label: t('pdfPageEditor.styles.enhance') },
        { id: 'grayscale', label: t('pdfPageEditor.styles.grayscale') },
        { id: 'bw', label: t('pdfPageEditor.styles.bw') },
        { id: 'sepia', label: t('pdfPageEditor.styles.sepia') },
        { id: 'vivid', label: t('pdfPageEditor.styles.vivid') },
        { id: 'soft', label: t('pdfPageEditor.styles.soft') },
    ];

    const activeStyle = color.colorStyle === 'custom' ? null : color.colorStyle;

    return (
        <div className="flex-none space-y-3 border-t border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
            <div>
                <p className="mb-2 text-xs font-semibold text-[#6B635C] dark:text-zinc-400">
                    {t('pdfPageEditor.colorStyles')}
                </p>
                <div className="flex flex-wrap gap-1.5">
                    {styleButtons.map((btn) => {
                        const selected = activeStyle === btn.id
                            || (btn.id === 'none' && !pageHasColorAdjust(color) && color.colorStyle !== 'custom');
                        return (
                            <button
                                key={btn.id}
                                type="button"
                                onClick={() => applyStyle(btn.id)}
                                className={`rounded-lg px-2.5 py-1.5 text-[12px] font-semibold transition-colors ${
                                    selected
                                        ? 'bg-pdf-cat-solid text-white'
                                        : 'bg-[#F7F5F3] text-[#3D3833] hover:bg-[#ECE8E4] dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700'
                                }`}
                            >
                                {btn.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
                <label className="block space-y-1.5">
                    <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                        {t('pdfPageEditor.brightness', { value: toBnDigits(color.brightness) })}
                    </span>
                    <input
                        type="range"
                        min={50}
                        max={150}
                        step={1}
                        value={color.brightness}
                        onChange={(e) => setField({ brightness: Number(e.target.value) })}
                        className="w-full accent-pdf-cat-solid"
                    />
                </label>
                <label className="block space-y-1.5">
                    <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                        {t('pdfPageEditor.contrast', { value: toBnDigits(color.contrast) })}
                    </span>
                    <input
                        type="range"
                        min={50}
                        max={180}
                        step={1}
                        value={color.contrast}
                        onChange={(e) => setField({ contrast: Number(e.target.value) })}
                        className="w-full accent-pdf-cat-solid"
                    />
                </label>
                <label className="block space-y-1.5">
                    <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                        {t('pdfPageEditor.saturation', { value: toBnDigits(color.saturation) })}
                    </span>
                    <input
                        type="range"
                        min={0}
                        max={200}
                        step={1}
                        value={color.saturation}
                        onChange={(e) => setField({ saturation: Number(e.target.value) })}
                        className="w-full accent-pdf-cat-solid"
                    />
                </label>
                <label className="block space-y-1.5">
                    <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                        {t('pdfPageEditor.grayscale', { value: toBnDigits(color.grayscale) })}
                    </span>
                    <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={color.grayscale}
                        onChange={(e) => setField({ grayscale: Number(e.target.value) })}
                        className="w-full accent-pdf-cat-solid"
                    />
                </label>
            </div>

            <div className="flex items-center justify-between gap-2">
                <div>
                    {showReset ? (
                        <button
                            type="button"
                            onClick={onReset}
                            className="rounded-xl border border-[#ECE8E4] bg-white px-4 py-2 text-sm font-semibold text-[#D33A2C] transition-colors hover:border-red-200 dark:border-zinc-700 dark:bg-zinc-900"
                        >
                            {t('pdfPageEditor.removeColor')}
                        </button>
                    ) : null}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="rounded-xl border border-[#ECE8E4] bg-white px-4 py-2 text-sm font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={onSave}
                        className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
                    >
                        {t('common.save')}
                    </button>
                </div>
            </div>
        </div>
    );
}
